import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import TextInput from '../TextInput';
import CountryCodes from './CountryCodes';
import { Wrapper, InputContainer, CountryFlag, CountryCodeInput } from './styled';

/**
 * Phone number input
 * leading zeros in country code replaced with ""
 * Phone country code and phone number separated
 */

const COUNTRY_CODE_MAX_LENGTH = 5;

const PhoneInput = ({ variant, disabled, value, onChange, formik }) => {
  const [isFlagVisible, setIsFlagVisible] = useState(false);
  const [flagCountryCode, setFlagCountryCode] = useState(null);

  const { phoneCountryCode, phoneNumber } = value || formik.values;

  const handleChange = (changedValue, name) => {
    let newPhoneCountryCode = phoneCountryCode;
    let newPhoneNumber = phoneNumber;
    if (name === 'phoneCountryCode') {
      newPhoneCountryCode = changedValue;
      if (newPhoneCountryCode.length && !newPhoneCountryCode.startsWith('+')) {
        newPhoneCountryCode = `+${newPhoneCountryCode}`;
      }
      if (newPhoneCountryCode && newPhoneCountryCode.length > COUNTRY_CODE_MAX_LENGTH) {
        newPhoneCountryCode = newPhoneCountryCode.slice(0, COUNTRY_CODE_MAX_LENGTH);
      }
    } else {
      newPhoneNumber = changedValue;
      // remove leading zeros
      newPhoneNumber = newPhoneNumber.replace(/^0+/, '');
      // remove non digits
      newPhoneNumber = newPhoneNumber.replace(/\D+/g, '');
      const phoneNumberMaxLength = phoneCountryCode ? 16 - phoneCountryCode?.length : 14;
      if (changedValue && changedValue.length > phoneNumberMaxLength) {
        newPhoneNumber = changedValue.slice(0, phoneNumberMaxLength);
      }
    }

    if (formik) {
      formik.setFieldValue('phoneCountryCode', newPhoneCountryCode);
      formik.setFieldValue('phoneNumber', newPhoneNumber);
      return;
    }

    onChange({ phoneCountryCode: newPhoneCountryCode, phoneNumber: newPhoneNumber });
  };

  const getErrorMessages = (name) => formik?.touched?.[name] && formik?.errors?.[name];
  const handleBlur = (name) =>
    formik && formik.setTouched({ ...formik.touched, [name]: true });

  useEffect(() => {
    if (phoneCountryCode !== '') {
      const obj = CountryCodes.find((o) => o.dial_code === phoneCountryCode);
      setFlagCountryCode(obj ? obj.code : null);
      setIsFlagVisible(!!obj);
    }
  }, [phoneCountryCode]);

  return (
    <Wrapper>
      <InputContainer>
        <CountryCodeInput
          showLabel
          $isFlagVisible={isFlagVisible}
          type="tel-country-code"
          autoComplete="tel-country-code"
          inputMode="numeric"
          label="Country code"
          name="phoneCountryCode"
          maxLength={COUNTRY_CODE_MAX_LENGTH}
          variant={variant}
          onChange={(val) => handleChange(val, 'phoneCountryCode')}
          onBlur={() => handleBlur('phoneCountryCode')}
          value={phoneCountryCode?.value ?? phoneCountryCode}
          validationErrorMessage={getErrorMessages('phoneCountryCode')}
        />
        {isFlagVisible && (
          <CountryFlag
            isFlagVisible={isFlagVisible}
            position="absolute"
            code={flagCountryCode}
            size={16}
          />
        )}
        <TextInput
          type="tel-national"
          autoComplete="tel-national"
          label="Phone number"
          variant={variant}
          onBlur={() => handleBlur('phoneNumber')}
          onChange={(val) => handleChange(val, 'phoneNumber')}
          inputMode="numeric"
          value={phoneNumber}
          name="phoneNumber"
          disabled={disabled}
          validationErrorMessage={getErrorMessages('phoneNumber')}
        />
      </InputContainer>
    </Wrapper>
  );
};

PhoneInput.propTypes = {
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  value: PropTypes.object,
  onChange: PropTypes.func,
  formik: PropTypes.object
};

PhoneInput.defaultProps = {
  variant: 'dark'
};

export default PhoneInput;
